import styled from '@emotion/styled';
import React, { ReactNode, useEffect, useState } from 'react';
import { Intercom } from 'react-live-chat-loader';
import { useRouter } from 'next/router';

import { FooterContainer } from './ServicePageTemplate';
import Footer from 'presentations/views/components/organisms/Footer';
import {
  blue02,
  colorPalette,
  gray05,
  white,
} from 'presentations/views/styles/color';
import {
  spacing,
  mediaMax,
  zIndex,
  isMediaMax,
} from 'presentations/views/styles';
import Progress from 'presentations/views/components/atoms/Progress';
import Transition from 'presentations/views/components/atoms/Transition';
import DashboardBottomNavigation from 'presentations/views/components/molecules/DashboardBottomNavigation';
import {
  isInElectron,
  isiOSApp as isiOSAppFunc,
} from 'presentations/views/helpers/electron';
import { apiPath } from 'presentations/routes/path';
import { post } from 'infras/rest_client';

interface Props {
  header: ReactNode;
  contentHeader?: ReactNode;
  content: ReactNode;
  isLoading?: boolean;
  snackbar?: ReactNode;
  isFooterHidden?: boolean;
  isVisibleSupport?: boolean;
  isMobileTabHidden?: boolean;
  showContentHeaderInElectron?: boolean;
}
export default function Component(props: Props) {
  const router = useRouter();
  const isMobile = isMediaMax('tablet');

  const [isiOSApp, setIsiOSApp] = useState<boolean>(false);

  useEffect(() => {
    setIsiOSApp(isiOSAppFunc());
  }, []);

  // iOSアプリからのイベントを処理
  useEffect(() => {
    if (!isiOSApp) return;

    // iOSアプリでrouter遷移させる
    const routerReplace = (event: CustomEvent) => {
      // /dashboardなど相対パスのみ許可
      if (typeof event.detail === 'string' && event.detail.startsWith('/')) {
        router.replace(event.detail);
      }
    };
    window.addEventListener<any>('jicooApp:routerReplace', routerReplace);

    const routerPush = (event: CustomEvent) => {
      // /dashboardなど相対パスのみ許可
      if (typeof event.detail === 'string' && event.detail.startsWith('/')) {
        router.push(event.detail);
      }
    };
    window.addEventListener<any>('jicooApp:routerPush', routerPush);

    // FCMトークンを送る
    const sendFcmToken = (event: CustomEvent) => {
      if (typeof event.detail === 'string') {
        post(apiPath('/v1/user_notification_tokens/create'), {
          data: { deviceType: 2, token: event.detail },
        });
      }
    };
    window.addEventListener<any>('jicooApp:sendFcmToken', sendFcmToken);

    return () => {
      window.removeEventListener<any>('jicooApp:routerReplace', routerReplace);
      window.removeEventListener<any>('jicooApp:routerPush', routerPush);
      window.removeEventListener<any>('jicooApp:sendFcmToken', sendFcmToken);
    };
  }, [isiOSApp]);

  return (
    <MainContainer>
      {!isiOSApp && <HeaderContainer>{props.header}</HeaderContainer>}
      {props.contentHeader && (
        <ContentHeader>
          {isInElectron() && !props.showContentHeaderInElectron
            ? null
            : props.contentHeader}
        </ContentHeader>
      )}
      <Content>
        <ContentInner>
          {props.isLoading && (
            <ProgressContainer>
              <Progress />
            </ProgressContainer>
          )}
          <Transition name="fade" in={!props.isLoading}>
            <Inner>{props.content}</Inner>
          </Transition>
        </ContentInner>
      </Content>
      {!props.isFooterHidden && !isiOSApp && (
        <FooterContainer>
          <Footer isThin={true} />
        </FooterContainer>
      )}
      {props.snackbar}
      {props.isVisibleSupport && <Intercom color={colorPalette.primary.www} />}
      {isMobile && !props.isMobileTabHidden && !isiOSApp && (
        <DashboardBottomNavigation />
      )}
    </MainContainer>
  );
}

export const MainContainer = styled.div`
  min-height: 100vh;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;
const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex(1100)};
  background-color: ${white};
  border-bottom: 1px solid ${gray05};
`;
const ProgressContainer = styled.div`
  width: 100%;
  height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ContentHeader = styled.div`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  ${mediaMax.desktop} {
    padding: 0 ${spacing(24)};
  }
  ${mediaMax.xs} {
    width: 100%;
  }
`;
const Content = styled.div`
  background: ${blue02};
  padding: ${spacing(48)} ${spacing(24)};
  flex: 1;

  ${mediaMax.tablet} {
    padding: ${spacing(24)} ${spacing(12)} ${spacing(64)};
  }
`;
const ContentInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;
const Inner = styled.div``;
